/**
 * Sawyer B1 + C1 — what Quick Book says after the 201 when part of what the
 * desk asked for did not land. Pure: no React, no I/O.
 *
 * The booking is written first; the bed (a second call) and the services (in
 * the same POST, after the booking) can each lose a race the desk could not
 * see. DESIGN-PHILOSOPHY #5 (never lie about data): a bed or a service that
 * did not land is said in a sentence naming why, never dropped silently and
 * never only in a toast. An empty list means everything landed and the card
 * closes as it always has.
 */
import type { AdditionalServicesWire } from './quick-book-services-api.js';

/** A service ticked on the card — its label is the one the desk read. */
export interface SelectedService {
  kind: 'addOn' | 'service';
  id: string;
  label: string;
  /** Boarding only: one leg on every day of the stay. */
  everyDay: boolean;
}

export interface PostBookedNote {
  key: string;
  title: string;
  body: string;
}

function sentence(s: string): string {
  const t = s.trim();
  return /[.!?]$/.test(t) ? t : `${t}.`;
}

export function postBookedNotes(args: {
  /** The dog(s) as the desk calls them — "Charles", "Charlie & Ruby". */
  dogLabel: string;
  bed: { asked: boolean; error: string | null };
  services: { asked: readonly SelectedService[]; response: AdditionalServicesWire | undefined };
}): PostBookedNote[] {
  const notes: PostBookedNote[] = [];

  if (args.bed.asked && args.bed.error) {
    notes.push({
      key: 'bed',
      title: 'Booked — the bed was not held',
      body: `${sentence(args.bed.error)} ${args.dogLabel} ${
        args.dogLabel.includes(' & ') ? 'are' : 'is'
      } under 'Not yet assigned to a bed' on the Lodging calendar — pick a bed there.`,
    });
  }

  if (args.services.asked.length > 0) {
    if (args.services.response === undefined) {
      // An older server strips the field and books without the services.
      notes.push({
        key: 'services-missing',
        title: 'Booked — the services were not added',
        body: 'Add them from the visit (Add a service).',
      });
    } else {
      for (const asked of args.services.asked) {
        const item = args.services.response.find((r) =>
          asked.kind === 'addOn' ? r.add_on_id === asked.id : r.service_type_id === asked.id,
        );
        if (item && item.added) continue;
        notes.push({
          key: `service:${asked.id}`,
          title: `Booked — ${asked.label} was not added`,
          body: 'Add it from the visit (Add a service).',
        });
      }
    }
  }

  return notes;
}
